import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";
import { Check, Sparkles } from "lucide-react";

const FEATURES = [
  "AI-powered natural-language talent search", "Evidence-backed candidate recommendations",
  "Authorized résumé access", "Candidate comparison & shortlists",
  "Direct messaging & interview requests", "Hiring workspace", "Team Assembly support",
];

export default function Pricing() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/public/pricing").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div className="relative">
      <Navbar />
      <section className="pt-32 pb-24 relative">
        <div className="absolute inset-0 ta-grid-radial opacity-40" aria-hidden />
        <div className="mx-auto max-w-6xl px-5 lg:px-8 relative">
          <p className="label-mono text-azure mb-3">Company access plans</p>
          <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl max-w-3xl">
            Access to a deeply vetted global technical talent network.
          </h1>
          <p className="mt-5 text-muted max-w-xl">
            Only approved companies can purchase access. Choose the plan that fits how you build.
          </p>

          {data && !data.visible && (
            <div className="mt-12 rounded-2xl border border-white/10 bg-surface p-10 text-center">
              <p className="font-display text-2xl font-bold">Invitation-only pricing</p>
              <p className="mt-2 text-muted">Please contact us to discuss access for your company.</p>
              <button onClick={() => nav("/register?role=startup_employer")} className="mt-6 rounded-full bg-azure px-6 py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">Contact Us</button>
            </div>
          )}

          {data && data.visible && (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
              {data.plans.map((p) => (
                <div key={p.key}
                  className={`relative rounded-2xl border bg-surface p-8 flex flex-col ${p.recommended ? "border-azure/50 ta-accent-glow" : "border-white/10"}`}
                  data-testid={`plan-${p.key}`}>
                  {p.recommended && (
                    <span className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-azure px-3 py-1 text-[11px] font-medium text-white">
                      <Sparkles size={11} /> Recommended
                    </span>
                  )}
                  <p className="label-mono text-faint">{p.name}</p>
                  <p className="mt-4 font-display text-4xl font-bold text-ink">{p.price_display}</p>
                  <p className="mt-3 text-sm text-muted min-h-[40px]">{p.description}</p>
                  {p.key === "annual" && <p className="mt-2 text-xs text-faint font-mono">12 months of access from activation. Not one-time or lifetime.</p>}
                  {p.key === "lifetime" && <p className="mt-2 text-xs text-faint font-mono">Access for the lifetime of the Team Assembly service, subject to terms.</p>}
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {FEATURES.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-muted">
                        <Check size={15} className="text-emerald mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => nav("/register?role=startup_employer")} data-testid={`plan-cta-${p.key}`}
                    className={`mt-8 rounded-full px-6 py-3 text-sm font-medium transition-colors duration-200 ${p.recommended ? "bg-azure text-white hover:bg-white hover:text-black" : "border border-white/15 text-ink hover:bg-white hover:text-black"}`}>
                    {p.button_text}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 rounded-xl border border-white/10 bg-surface/50 p-6 max-w-4xl">
            <p className="text-sm text-ink font-medium">Annual → Lifetime upgrade</p>
            <p className="mt-1 text-sm text-muted">
              Companies with active annual access can upgrade to lifetime by paying the difference
              {data?.upgrade ? ` ($${data.upgrade.amount.toLocaleString()})` : ""}. Your original annual payment is preserved.
            </p>
          </div>

          <p className="mt-6 text-xs text-faint max-w-4xl leading-relaxed" data-testid="pricing-legal-disclosure">
            Team Assembly is a product of Talent Junction, Inc. Payments and invoices are processed and issued by Talent Junction, Inc.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
